import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { SubjectProvider } from "./contexts/SubjectContext";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Header from "@/components/Header";
import Home from "@/pages/Home";
import Quiz from "@/pages/Quiz";
import Statistics from "@/pages/Statistics";
import Review from "@/pages/Review";
import CustomQuiz from "@/pages/CustomQuiz";
import TeacherDashboard from "@/pages/TeacherDashboard";
import TeacherWorksheet from "@/pages/TeacherWorksheet";
import TeacherStarter from "@/pages/TeacherStarter";
import TeacherBanks from "@/pages/TeacherBanks";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthPage from "@/pages/Auth";
import OnboardingPage from "@/pages/Onboarding";
import NotFound from "@/pages/not-found";
import Drills from "@/pages/Drills";
import CustomQuizStart from "@/pages/CustomQuizStart";
import KeepLoggedInPrompt from "@/components/KeepLoggedInPrompt";
import { useLocation } from "wouter";
import { useEffect } from "react";

function Router() {
  const [location, setLocation] = useLocation();

  // Don't redirect if we're already on a valid route
  useEffect(() => {
    // Only redirect to homepage on truly empty/invalid paths
    // Allow all defined routes to work normally
    const validRoutes = [
      '/',
      '/auth',
      '/onboarding',
      '/statistics',
      '/review',
      '/custom-quiz',
      '/custom-quiz/start',
      '/drills',
      '/teacher-dashboard',
      '/teacher/worksheet',
      '/teacher/starter',
      '/teacher/banks',

      '/admin/dashboard'
    ];

    const isValidRoute = validRoutes.some(route => location === route) ||
      location.startsWith('/quiz/') ||
      location.startsWith('/custom-quiz') ||
      location.startsWith('/admin');

    // Only redirect if on an invalid/empty route
    if (!isValidRoute && location !== '/') {
      setLocation('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isQuizPage = location.startsWith("/quiz/");
  const isStatsPage = location === "/statistics";
  const isReviewPage = location === "/review";
  const isAuthPage = location === "/auth";
  const isOnboardingPage = location === "/onboarding";
  const isCustomQuizPage = location.startsWith("/custom-quiz");
  const isTeacherDashboard = location.startsWith("/teacher-dashboard");
  const isAdminDashboard = location === "/admin/dashboard";
  const isTeacherBanks = location === "/teacher/banks";

  // Hide header on auth and onboarding pages
  const showHeader = !isAuthPage && !isOnboardingPage;     

  return (
    <div className="min-h-screen flex flex-col">
      {showHeader && (
        <Header
          onHomeClick={() => setLocation("/")}
          showHome={isQuizPage || isStatsPage || isReviewPage || isCustomQuizPage || isTeacherDashboard || isAdminDashboard || isTeacherBanks}
        />
      )}
      <main className="flex-1">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/auth" component={AuthPage} />
          <Route path="/onboarding" component={OnboardingPage} />
          <Route path="/quiz/:topicId" component={Quiz} />
          <Route path="/statistics" component={Statistics} />
          <Route path="/review" component={Review} />
          <Route path="/custom-quiz/start" component={CustomQuizStart} />
          <Route path="/custom-quiz" component={CustomQuiz} />
          <Route path="/drills" component={Drills} />
          <Route path="/teacher-dashboard" component={TeacherDashboard} />
          <Route path="/teacher/worksheet" component={TeacherWorksheet} />
          <Route path="/teacher/starter" component={TeacherStarter} />
          <Route path="/teacher/banks" component={TeacherBanks} />
            <Route path="/admin/dashboard" component={AdminDashboard} />
          <Route component={NotFound} />
        </Switch>
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SubjectProvider>
        <TooltipProvider>
          <Toaster />
          <KeepLoggedInPrompt />
          <Router />
        </TooltipProvider>
      </SubjectProvider>
    </QueryClientProvider>
  );
}

export default App;
